import React, { useEffect, useState } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import SelectTree from '@app/components/select-tree/SelectTree';
import { NIL as NIL_UUID } from 'uuid';

import { messageError, messageToast } from '../../utils/define';
import { editHoTroAction, hoTroGetItemAction } from '../../store/reducers/hotro';
import { linhVucGetAllAction } from '../../store/reducers/linhvuc';
import { khoaGetAllAction } from '../../store/reducers/khoa';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';
import { getNguoiDungByAccountId, getNguoiDungById } from '../../store/reducers/nguoidung';
import { LOAI_LOI_OPTIONS, MUC_DO_PHUC_TAP_OPTIONS, mapLoaiLoiLabel, mapMucDoPhucTapLabel } from '../../store/constants';


const schema = yup.object().shape({
    nguoiDung: yup.string().required('Vui lòng nhập Người hỗ trợ'),
    linhVucId: yup.number().required('Vui lòng chọn lĩnh vực'),
    khoaIds: yup.array().min(1, 'Vui lòng chọn ít nhất 1 khoa'),
    ngayHoTro: yup.string().required(messageError.emptyMessage),
    loaiLoi: yup.string().required(messageError.emptyMessage),
    mucDoPhucTap: yup.string().required(messageError.emptyMessage),
    noiDung: yup.string().nullable(),
});

const HoTroEdit = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const hoTroItem = useSelector((state) => state.hoTro?.hoTroItem);
    const listLinhVuc = useSelector((state) => state.linhvuc.listLinhVuc || []);
    const listKhoa = useSelector((state) => state.khoa.listKhoa || []);

    console.log('hoTroItem:', hoTroItem);

    const [isLoading, setIsLoading] = useState(false);
    const [mappedNguoiDungId, setMappedNguoiDungId] = useState(null);
    const [mappedNguoiDungName, setMappedNguoiDungName] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get('id');
        if (id) dispatch(hoTroGetItemAction(id));
    }, [dispatch, location.search]);

    useEffect(() => {
        if (!listLinhVuc.length) dispatch(linhVucGetAllAction());
        if (!listKhoa.length) dispatch(khoaGetAllAction());
    }, [dispatch, listLinhVuc.length, listKhoa.length]);

    // fetch display name for nguoiDungId present in hoTroItem
    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!hoTroItem?.nguoiDungId) return;
            const id = hoTroItem.nguoiDungId;
            // set mapped id (the stored id)
            setMappedNguoiDungId(id);
            // try get by user id first
            try {
                const res = await dispatch(getNguoiDungById(id)).unwrap();
                let item = res || null;
                if (Array.isArray(item)) item = item[0] || null;
                if (item && item.data) item = item.data;
                if (Array.isArray(item)) item = item[0] || null;
                const name = item?.hoTen || item?.fullName || item?.name || item?.ten || item?.displayName || null;
                if (mounted && name) {
                    setMappedNguoiDungName(name);
                    return;
                }
            } catch (e) {
                // ignore
            }

            // fallback try by account id
            try {
                const res2 = await dispatch(getNguoiDungByAccountId(id)).unwrap();
                let item2 = res2 || null;
                if (Array.isArray(item2)) item2 = item2[0] || null;
                if (item2 && item2.data) item2 = item2.data;
                if (Array.isArray(item2)) item2 = item2[0] || null;
                const name2 = item2?.hoTen || item2?.fullName || item2?.name || item2?.ten || item2?.displayName || null;
                if (mounted && name2) setMappedNguoiDungName(name2);
            } catch (e) {
                // ignore
            }
        })();
        return () => { mounted = false; };
    }, [dispatch, hoTroItem]);

    const handleEdit = async (values) => {
        if (!hoTroItem?.id) {
            toast.error('Không tìm thấy Hỗ Trợ để cập nhật!');
            return;
        }

        // prefer mappedNguoiDungId (stored id) if available, otherwise user input
        const nguoiDungIdToSend = mappedNguoiDungId || values.nguoiDung || '00000000-0000-0000-0000-000000000000';
        const modelUpdate = {
            id: hoTroItem.id,
            data: {
                nguoiDungId: nguoiDungIdToSend,
                linhVucId: values.linhVucId,
                khoaIds: values.khoaIds,
                ngayHoTro: values.ngayHoTro,
                loaiLoi: values.loaiLoi,
                mucDoPhucTap: values.mucDoPhucTap,
                noiDung: values.noiDung,
                ghiChu: values.ghiChu,
                status: values.status ? 1 : 0,
            }
        };

        try {
            setIsLoading(true);
            const result = await dispatch(editHoTroAction(modelUpdate)).unwrap();
            if (result === 200) {
                toast.success(messageToast.messageToastUpdateSuccess);
                history.goBack();
            } else {
                toast.error(messageToast.messageToastUpdateError);
            }
        } catch (error) {
            console.error('Lỗi khi gọi editHoTroAction:', error);
            toast.error('Đã có lỗi xảy ra, vui lòng thử lại!');
        } finally {
            setIsLoading(false);
        }
    };

    if (!hoTroItem) return <ReactSpinner loadings={true} />;

    // Normalize values coming from API for form fields
    const normalizeDateForInput = (dateStr) => {
        if (!dateStr) return '';
        // If already in YYYY-MM-DD format, return as-is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        // If contains T (ISO datetime), split
        if (typeof dateStr === 'string' && dateStr.indexOf('T') !== -1) {
            return dateStr.split('T')[0];
        }
        // Fallback try to build from Date
        const dt = new Date(dateStr);
        if (isNaN(dt.getTime())) return '';
        const yyyy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const normalizeStatusToBoolean = (status) => {
        if (status === null || status === undefined) return false;
        const s = String(status).toLowerCase();
        if (s === '1' || s === 'true' || s === 'hoanthanh' || s === 'hoàn thành' || s === 'hoan_thanh') return true;
        return false;
    };

    const initialNguoiDungDisplay = mappedNguoiDungName || hoTroItem?.nguoiDungId || '';
    const initialValues = {
        nguoiDung: initialNguoiDungDisplay,
        linhVucId: hoTroItem?.linhVucId || '',
        khoaIds: Array.isArray(hoTroItem.khoaIds) ? hoTroItem.khoaIds : [],
        ngayHoTro: normalizeDateForInput(hoTroItem?.ngayHoTro),
        loaiLoi: hoTroItem?.loaiLoi || '',
        mucDoPhucTap: hoTroItem?.mucDoPhucTap || '',
        noiDung: hoTroItem?.noiDung || '',
        ghiChu: hoTroItem?.ghiChu || '',
        status: normalizeStatusToBoolean(hoTroItem?.status),
    };

    return (
        <Card>
            <Card.Header as="h5">Sửa Hỗ Trợ</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={handleEdit}
                    initialValues={initialValues}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            {/* Người hỗ trợ */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Người hỗ trợ</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        type="text"
                                        name="nguoiDung"
                                        value={values.nguoiDung}
                                        onChange={(e) => {
                                            // user changed display text -> clear mapped id
                                            setFieldValue('nguoiDung', e.target.value);
                                            setMappedNguoiDungId(null);
                                            setMappedNguoiDungName(e.target.value);
                                        }}
                                        isInvalid={!!errors.nguoiDung}
                                        placeholder="Nhập ID hoặc tên người hỗ trợ"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.nguoiDung}</Form.Control.Feedback>
                                    {/* keep id hidden in DOM for reference if needed */}
                                    <input type="hidden" name="nguoiDungId" value={mappedNguoiDungId || hoTroItem?.nguoiDungId || ''} />
                                </Col>
                            </Form.Group>

                            {/* Lĩnh vực */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Lĩnh vực</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="select"
                                        name="linhVucId"
                                        value={values.linhVucId || ''}
                                        onChange={(e) => setFieldValue('linhVucId', Number(e.target.value))}
                                        isInvalid={!!errors.linhVucId}
                                    >
                                        <option value="">-- Chọn lĩnh vực --</option>
                                        {listLinhVuc.map((lv) => (
                                            <option key={lv.id} value={lv.id}>{lv.name}</option>
                                        ))}
                                    </Form.Control>
                                    {errors.linhVucId && <div className="text-danger mt-1">{errors.linhVucId}</div>}
                                </Col>
                            </Form.Group>

                            {/* Khoa */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Khoa hỗ trợ
                                    {values.khoaIds?.length > 0 && (
                                        <span className="text-primary ms-1">
                                            ({values.khoaIds.length})
                                        </span>
                                    )}</Form.Label>
                                <Col sm="10">
                                    <SelectTree
                                        data={listKhoa.map((k) => ({
                                            id: k.id,
                                            name: k.name,
                                            parentId: k.parentId || NIL_UUID
                                        }))}
                                        showSearchBox
                                        Placeholder="Chọn khoa hỗ trợ"
                                        positionExpand="right"
                                        multiSelect
                                        multiValueSelected={values.khoaIds}
                                        // SelectTree expects multiValueSelected for preselected ids
                                        // (its internal prop name), not selectedIds/value
                                        onChange={(arr) => setFieldValue('khoaIds', arr.map(x => x.id))}
                                    />
                                    {errors.khoaIds && <div className="invalid-feedback d-block">{errors.khoaIds}</div>}
                                </Col>
                            </Form.Group>

                            {/* Ngày hỗ trợ */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Ngày hỗ trợ</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        type="date"
                                        name="ngayHoTro"
                                        value={values.ngayHoTro}
                                        onChange={handleChange}
                                        isInvalid={!!errors.ngayHoTro}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.ngayHoTro}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Loại lỗi</Form.Label>
                                <Col sm="10">
                                    <Form.Control as="select" name="loaiLoi" value={values.loaiLoi} onChange={(e) => setFieldValue('loaiLoi', e.target.value)} isInvalid={!!errors.loaiLoi}>
                                        <option value="">-- Chọn loại lỗi --</option>
                                        {LOAI_LOI_OPTIONS.map(opt => (
                                            <option key={opt.key} value={opt.key}>{opt.label}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.mucDo}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Mức độ phức tạp</Form.Label>
                                <Col sm="10">
                                    <Form.Control as="select" name="mucDoPhucTap" value={values.mucDoPhucTap} onChange={(e) => setFieldValue('mucDoPhucTap', e.target.value)} isInvalid={!!errors.mucDoPhucTap}>
                                        <option value="">-- Chọn loại lỗi --</option>
                                        {MUC_DO_PHUC_TAP_OPTIONS.map(opt => (
                                            <option key={opt.key} value={opt.key}>{opt.label}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.mucDo}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Nội dung */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Nội dung</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="textarea"
                                        rows={8}
                                        name="noiDung"
                                        value={values.noiDung}
                                        onChange={handleChange}
                                        isInvalid={!!errors.noiDung}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.noiDung}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Ghi chú */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Ghi chú</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="ghiChu"
                                        value={values.ghiChu}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Form.Group>

                            {/* Trạng thái */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Hoàn thành</Form.Label>
                                <Col sm="10" className="d-flex align-items-center">
                                    <Form.Check
                                        type="checkbox"
                                        name="status"
                                        checked={values.status}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Form.Group>

                            {/* Buttons */}
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup>
                                        <Button type="submit" theme="primary" disabled={isLoading}>Sửa</Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button theme="secondary" onClick={() => history.goBack()}>Hủy</Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>

                <ReactSpinner loadings={isLoading} />
            </Card.Body>
        </Card>
    );
};

export default HoTroEdit;
